import { isPlatformServer } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import {
    inject,
    isDevMode,
    NgModule,
    PLATFORM_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { LayoutModule } from '@core-app/layout/layout.module';
import { X_SERVICE_ZONE } from '@core-app/tokens';
import { ChatService } from '@gallaghercp/core-chat';
import {
    APIInterceptor,
    appReducer,
    CacheService,
    CoreModule,
    metaReducers,
    SettingsService,
    ZoneService,
} from '@gallaghercp/core-frontend';
import { APP_CONFIG_TOKEN, TRANSLATE_SERVICE } from '@gallaghercp/core-frontend-contracts';
import { TranslateService } from '@ngx-translate/core';
import { ICON_CONFIG } from '@gallaghercp/core-components/icon';
import { StoreModule } from '@ngrx/store';
import { from, Observable, switchMap, tap } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_CONFIG as LOCAL_APP_CONFIG } from './app.config';
import { ClientRoutingModule } from './client/client-routing.module';

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        ClientRoutingModule,
        AppRoutingModule,
        CoreModule,
        LayoutModule,
        StoreModule.forRoot({ app: appReducer }, { metaReducers }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode() && (LOCAL_APP_CONFIG as any).pwa?.enabled === true,
            registrationStrategy: 'registerImmediately',
        }),
    ],
    providers: [
        {
            provide: ICON_CONFIG,
            useValue: {
                baseUrl: ['/assets/img/icons/', '@gallaghercp/core-mediplan/src/lib/assets/icons'],
            },
        },
        provideZoneChangeDetection(),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withXsrfConfiguration({
                cookieName: 'XSRF-TOKEN',
                headerName: 'X-XSRF-TOKEN',
            }),
        ),
        // provideClientHydration(withNoHttpTransferCache()),
        // provideEnvironmentInitializer(() => {
        //     const platformId = inject(PLATFORM_ID);
        //     const config = LOCAL_APP_CONFIG as any;
        //     if (isPlatformBrowser(platformId) && config.pwa?.enabled && config.firebaseNotifications) {
        //         const app = initializeApp(config.firebaseNotifications);
        //         getMessaging(app); // activate messaging
        //     }
        // }),
        {
            provide: X_SERVICE_ZONE,
            useFactory: () => {
                const cacheService = inject(CacheService);
                const storedValue = cacheService.getFromStorage('x-service-zone');
                return storedValue ?? '1';
            },
        },
        {
            provide: APP_CONFIG_TOKEN,
            useValue: LOCAL_APP_CONFIG,
        },
        {
            provide: TRANSLATE_SERVICE,
            useExisting: TranslateService,
        },
        provideAppInitializer(() => {
            const initializerFn = initialiseAppConfig(
                inject(SettingsService),
                inject(PLATFORM_ID),
                inject(ZoneService),
                inject(ChatService),
                inject(APIInterceptor),
            );
            return initializerFn();
        }),
    ],
})
export class AppModule {}

export function initialiseAppConfig(
    settings: SettingsService,
    platformId: object,
    zones: ZoneService,
    chat: ChatService,
    apiInterceptor: APIInterceptor,
): () => Observable<any> {
    // Use zone route configuration from app.config zoneRouting (array of { id, route })
    ZoneService.zonalRoutes = (((LOCAL_APP_CONFIG as any).zoneRouting ?? []) as any[])
        .map(z => z?.route)
        .filter((r: any) => !!r) as string[];
    const config = { ...LOCAL_APP_CONFIG };
    if (!isPlatformServer(platformId) && (LOCAL_APP_CONFIG as any).enableSSR) {
        config.api = '/api/2.0';
    }
    return () =>
        from(settings.init(config)).pipe(
            switchMap(() => zones.retrieveEnabledZones()),
            tap(() => {
                chat.setApiInterceptor(apiInterceptor);
            }),
        );
}
